import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ShieldCheck, House, Receipt, Bank, TreeStructure, ChartBar, Users, Gear, Robot, SignOut, ClockCounterClockwise, Certificate, PlugsConnected, ChartLineUp, Files, Scales, Coins, RocketLaunch, Wallet, CreditCard, TrendDown, ChartPieSlice, FolderLock, Buildings, CurrencyEur, FileText, UsersThree, Eye
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import ConsentGate from "@/components/ConsentGate";
import { toast } from "sonner";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: House, tid: "nav-dashboard" },
  { to: "/setup", label: "Getting Started", icon: RocketLaunch, tid: "nav-setup" },
  { to: "/receipts", label: "Receipts", icon: Receipt, tid: "nav-receipts" },
  { to: "/bank", label: "Bank Import", icon: Bank, tid: "nav-bank" },
  { to: "/documents", label: "Documents", icon: Files, tid: "nav-documents" },
  { to: "/grants", label: "Grant Guardian", icon: TreeStructure, tid: "nav-grants" },
  { to: "/runway", label: "Cash Runway", icon: TrendDown, tid: "nav-runway" },
  { to: "/currency", label: "Currency", icon: CurrencyEur, tid: "nav-currency" },
  { to: "/invoices", label: "Invoices", icon: FileText, tid: "nav-invoices" },
  { to: "/compliance", label: "Compliance", icon: Scales, tid: "nav-compliance" },
  { to: "/tax-reliefs", label: "Tax Reliefs", icon: Coins, tid: "nav-tax-reliefs" },
  { to: "/payroll", label: "Payroll", icon: Wallet, tid: "nav-payroll" },
  { to: "/captable", label: "Cap Table", icon: ChartPieSlice, tid: "nav-captable" },
  { to: "/dataroom", label: "Data Room", icon: FolderLock, tid: "nav-dataroom" },
  { to: "/certificates", label: "Certificates", icon: Certificate, tid: "nav-certificates" },
  { to: "/integrations", label: "Integrations", icon: PlugsConnected, tid: "nav-integrations" },
  { to: "/statements", label: "Statements", icon: ChartBar, tid: "nav-statements" },
  { to: "/analytics", label: "Analytics", icon: ChartLineUp, tid: "nav-analytics" },
  { to: "/accountants", label: "Accountant", icon: Users, tid: "nav-accountants" },
  { to: "/team", label: "Team", icon: UsersThree, tid: "nav-team" },
  { to: "/advisor", label: "Compliance AI", icon: Robot, tid: "nav-advisor" },
  { to: "/audit", label: "Audit Log", icon: ClockCounterClockwise, tid: "nav-audit" },
  { to: "/billing", label: "Billing", icon: CreditCard, tid: "nav-billing" },
  { to: "/settings", label: "Settings", icon: Gear, tid: "nav-settings" },
];

export default function AppShell() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [isAccountant, setIsAccountant] = useState(false);

  useEffect(() => {
    api.get("/accountant/clients").then((r) => setIsAccountant((r.data.clients || []).length > 0)).catch(() => {});
  }, []);

  // Auto-accept a team invite for an already-signed-in user (link opened while logged in).
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("team_invite") || sessionStorage.getItem("pc_team_invite");
    if (!token) return;
    sessionStorage.removeItem("pc_team_invite");
    api.post("/team/accept", { token })
      .then((r) => { toast.success(`You've joined ${r.data.company_name}`); refresh && refresh(); })
      .catch(() => {});
  }, [refresh]);

  const navItems = isAccountant
    ? [...nav, { to: "/portal", label: "Client Portal", icon: Buildings, tid: "nav-portal" }]
    : [...nav];
  if (user?.is_admin) navItems.push({ to: "/admin", label: "Admin", icon: ShieldCheck, tid: "nav-admin" });

  return (
    <div className="min-h-screen aurora grain flex">
      <ConsentGate />
      {/* Sidebar */}
      <aside className="w-[260px] shrink-0 border-r border-white/5 bg-black/30 backdrop-blur-xl min-h-screen sticky top-0 hidden lg:flex flex-col">
        <div className="px-6 py-6 hairline-b">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#FFB020" }}>
              <ShieldCheck size={22} weight="duotone" color="#050B0A" />
            </div>
            <div>
              <div className="font-display leading-none">PerCen360</div>
              <div className="text-[9px] tracking-[0.24em] uppercase text-white/45 mt-1">Compliance OS</div>
            </div>
          </div>
        </div>
        <nav className="sidenav flex-1 px-3 py-5 space-y-1">
          {navItems.map(n => (
            <NavLink key={n.to} to={n.to} data-testid={n.tid} className={({ isActive }) => isActive ? "active" : ""}>
              <n.icon size={18} weight="duotone" /> <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 p-2 rounded-xl bg-white/5">
            {user?.picture ? <img alt="" src={user.picture} className="w-8 h-8 rounded-full" /> :
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs">{user?.name?.[0] || "U"}</div>}
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-[10px] text-white/50 truncate">{user?.email}</div>
            </div>
            <button data-testid="btn-logout" onClick={logout} className="text-white/60 hover:text-white p-1" title="Logout">
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile header */}
        <div className="lg:hidden hairline-b bg-black/50 backdrop-blur-xl sticky top-0 z-40 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#FFB020" }}>
              <ShieldCheck size={18} weight="duotone" color="#050B0A" />
            </div>
            <div className="font-display text-sm">PerCen360</div>
          </div>
          <button onClick={logout} className="text-white/60"><SignOut size={18} /></button>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-8 lg:py-12">
          {user?.can_edit === false && (
            <div data-testid="readonly-banner" className="mb-6 flex items-center gap-2 rounded-xl border border-[#AEB8B4]/25 bg-white/5 px-4 py-2.5 text-sm text-white/70">
              <Eye size={16} weight="fill" /> You have <strong className="text-white/90">read-only</strong> access as an accountant. Ask an owner or admin to change your role to make edits.
            </div>
          )}
          <Outlet />
        </div>

        <footer className="border-t border-white/5 mt-16 py-6">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/45">
            <div>percen360.com · EU region · GDPR compliant</div>
            <div className="flex gap-5">
              <Link to="/privacy" className="hover:text-white/80">Privacy</Link>
              <Link to="/terms" className="hover:text-white/80">Terms</Link>
              <Link to="/dpia" className="hover:text-white/80">DPIA</Link>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
